import { Injectable, inject } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, Scroll } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Router scroll handling that scrolls to the top only when the route path changes.
 *
 * Views such as the Pending FTE Review View store their filters in query parameters,
 * so every filter change is a navigation. Angular's `scrollPositionRestoration: 'enabled'`
 * would scroll to the top on each of them and pull open sidebar dropdowns off-screen.
 * Back/forward navigations still restore the saved position, and anchors still scroll.
 */
@Injectable({ providedIn: 'root' })
export class QueryParamScrollService {
  private readonly viewportScroller = inject(ViewportScroller);
  private lastPath: string | null = null;

  constructor() {
    inject(Router)
      .events.pipe(
        filter((event): event is Scroll => event instanceof Scroll),
        takeUntilDestroyed(),
      )
      .subscribe((event) => this.onScroll(event));
  }

  private onScroll(event: Scroll): void {
    const { routerEvent } = event;
    const url =
      routerEvent instanceof NavigationEnd ? routerEvent.urlAfterRedirects : routerEvent.url;
    const path = url.split(/[?#]/)[0];
    const pathChanged = path !== this.lastPath;
    this.lastPath = path;

    if (event.position) {
      this.viewportScroller.scrollToPosition(event.position);
    } else if (event.anchor) {
      this.viewportScroller.scrollToAnchor(event.anchor);
    } else if (pathChanged) {
      this.viewportScroller.scrollToPosition([0, 0]);
    }
  }
}
